<template>
  <v-dialog
    v-model="dialog"
    width="448"
    open-on-focus
    no-click-animation
    persistent
    content-class="new-practice-dialog"
    @click:outside="onClickOutside"
    @keydown="onKeydown"
  >
    <v-card elevation="0">
      <v-form ref="form" class="new-practice-form" @submit.prevent="onSubmit">
        <div class="new-practice-form-body">
          <v-toolbar flat height="76">
            <v-toolbar-title class="new-practice-toolbar-title"
              >Add Participant</v-toolbar-title
            >
            <v-spacer></v-spacer>
            <v-btn color="accent" icon tile @click="closeDialog">
              <v-icon size="24">mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <!-- practice -->
          <v-card-title class="new-practice-card-title pb-0"
            >User data</v-card-title
          >
          <v-container class="new-practice-card-container pt-0">
            <v-row>
              <v-col sm="6">
                <dialog-input
                  label="First name"
                  name="firstName"
                  @input="onInput"
                />
              </v-col>
              <v-col sm="6">
                <dialog-input
                  label="Last name"
                  name="lastName"
                  @input="onInput"
                />
              </v-col>
            </v-row>
          </v-container>
          <v-container class="new-practice-card-container pt-0">
            <dialog-input
              label="Email address"
              name="email"
              prepend-icon="mdi-email-outline"
              :validate="true"
              :validationType="'email'"
              @input="onInput"
            />
          </v-container>
          <v-container class="new-practice-card-container py-0">
            <dialog-input
              label="Phone"
              name="phone"
              :validate="true"
              :validationType="'digits'"
              prepend-icon="mdi-phone-outline"
              @input="onInput"
            />
          </v-container>
          <v-container class="new-practice-card-container pb-0">
            <dialog-select
              class="mb-3"
              label="Status"
              name="status"
              v-model="newPatientData.status"
              :items="participantStatuses"
              item-text="text"
              item-value="value"
              @input="onInput"
              clearable
            />
          </v-container>
          <v-container class="new-practice-card-container">
            <dialog-select
              v-if="!practiceUserId"
              label="Advisor"
              name="practiceUserId"
              :items="advisors"
              item-text="fullName"
              item-value="id"
              @input="onInput"
              :value="newPatientData.practiceUserId"
            />
          </v-container>
          <v-card-title class="new-practice-card-title pb-0 d-none"
            >Feature Settings</v-card-title
          >
          <v-container class="new-practice-card-container py-0 d-none">
            <bh-select
              class="mb-3"
              :disabled="!isAnyAdminRole"
              :label="tenantTexts.addPracticePatientDialog.swapFlag"
              v-model="newPatientData.swapAvailable"
              :items="computedFeatureFlagsSwap"
              item-text="text"
              item-value="value"
              @input="changeFeatureFlag('swapAvailable', $event)"
            />
          </v-container>
          <v-container
            class="new-practice-card-container py-0 d-none"
            v-if="featuresSettings.VUE_FEATURE_SMART_SURVEY"
          >
            <bh-select
              class="mb-3"
              :disabled="!isAnyAdminRole"
              :label="tenantTexts.addPracticePatientDialog.smartSurveyFlag"
              v-model="newPatientData.smartSurveyAvailable"
              :items="computedFeatureFlagsSmartSurvey"
              item-text="text"
              item-value="value"
              @input="changeFeatureFlag('smartSurveyAvailable', $event)"
            />
          </v-container>
          <v-container class="new-practice-card-container py-0 d-none">
            <bh-select
              class="mb-3"
              :disabled="!roleAllowedToManageJournalFlag"
              :label="tenantTexts.addPracticePatientDialog.journalFlag"
              v-model="newPatientData.journalAvailable"
              :items="computedFeatureFlagsJournal"
              item-text="text"
              item-value="value"
              @input="changeFeatureFlag('journalAvailable', $event)"
            />
          </v-container>
          <v-container
            class="new-practice-card-container py-0 d-none"
            v-if="featuresSettings.VUE_FEATURE_SCREENER"
          >
            <bh-select
              class="mb-3"
              :disabled="!isAnyAdminRole"
              :label="tenantTexts.addPracticePatientDialog.surveyFlag"
              v-model="newPatientData.surveyAvailable"
              :items="computedFeatureFlagsSurvey"
              item-text="text"
              item-value="value"
              @input="changeFeatureFlag('surveyAvailable', $event)"
            />
          </v-container>
        </div>
        <v-container class="new-practice-card-footer d-flex justify-end pt-0">
          <v-btn
            :disabled="!formIsValid"
            rounded
            color="primary"
            type="submit"
            elevation="0"
          >
            Add participant
          </v-btn>
        </v-container>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import DialogInput from '@/components/forms/DialogInput.vue'
import DialogSelect from '@/components/forms/DialogSelect.vue'
import jwt_decode from 'jwt-decode'
import { mapState, mapGetters, mapActions } from 'vuex'
import BhSelect from '@/components/inputs/BhSelect.vue'
import TenantTextsMixin from '@/mixins/tenantTexts'
import FeaturesSettingsMixin from '@/mixins/featuresSettings'
import { DEFAULT_LANGUAGE } from '../../helpers/constants'

const emptyForm = {
  firstName: '',
  lastName: '',
  address: '',
  practiceId: 0,
  email: '',
  isActive: true,
  phone: '',
  practiceUserId: null,
  journalAvailable: 'Active',
  smartSurveyAvailable: 'Active',
  swapAvailable: 'Active',
  surveyAvailable: 'Inactive',
  language: DEFAULT_LANGUAGE,
}

export default {
  name: 'NewPracticePatientDialog',
  components: {
    DialogInput,
    DialogSelect,
    BhSelect,
  },
  mixins: [TenantTextsMixin, FeaturesSettingsMixin],
  props: {
    swapAvailable: {
      type: String,
      default: 'Active',
    },
    smartSurveyAvailable: {
      type: String,
      default: 'Active',
    },
    journalAvailable: {
      type: String,
      default: 'Active',
    },
    surveyAvailable: {
      type: String,
      default: 'Inactive',
    },
    showDialog: {
      type: Boolean,
      default: false,
    },
    practiceId: {
      type: Number,
      default: 0,
    },
    practiceUserId: {
      type: [Number, null],
      default: () => null,
    },
  },
  data() {
    return {
      dialog: false,
      newPatientData: {
        ...emptyForm,
        practiceId: this.practiceId,
        practiceUserId: this.practiceUserId,
        journalAvailable: this.journalAvailable || emptyForm.journalAvailable,
        smartSurveyAvailable:
          this.smartSurveyAvailable || emptyForm.smartSurveyAvailable,
        swapAvailable: this.swapAvailable || emptyForm.swapAvailable,
        surveyAvailable: this.surveyAvailable || emptyForm.surveyAvailable,
      },
      rules: {
        text: [v => !!v || 'Value cannot by empty'], // @todo
      },
      practiceDoctors: [],
    }
  },
  watch: {
    swapAvailable: function(val) {
      if (val) {
        this.newPatientData.swapAvailable = val
      }
    },
    smartSurveyAvailable: function(val) {
      if (val) {
        this.newPatientData.smartSurveyAvailable = val
      }
    },
    journalAvailable: function(val) {
      if (val) {
        this.newPatientData.journalAvailable = val
      }
    },
    surveyAvailable: function(val) {
      if (val) {
        this.newPatientData.surveyAvailable = val
      }
    },
    async showDialog(val) {
      if (val) {
        this.dialog = true
        const doctors = await this.getPracticeUsers({
          id: this.practiceId,
          params: this.doctorsQuery,
          returnData: true,
        })

        this.practiceDoctors = doctors || []
      }
    },
  },
  computed: {
    ...mapState('dictionariesStates', [
      'dictionaryBhUsersRoleIds',
      'dictionaryPracticeUserRole',
      'dictionaryFeatureFlagState',
    ]),
    ...mapGetters('userStates', ['getRolename', 'getToken']),
    isAnyAdminRole() {
      return (
        this.dictionaryBhUsersRoleIds[this.getRolename] ===
          this.dictionaryBhUsersRoleIds.Admin ||
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Administrator
      )
    },
    participantStatuses() {
      return [
        { text: 'Tech Challenged', value: 'TECH_CHALLENGED' },
        { text: 'Home Challenged', value: 'HOME_CHALLENGED' },
        { text: 'Communication Challenged', value: 'COMMUNICATION_CHALLENGED' },
        { text: 'Resistance to Change', value: 'RESISTANCE_TO_CHANGE' },
        { text: 'Actively Changing', value: 'ACTIVELY_CHANGING' },
      ]
    },
    roleAllowedToManageJournalFlag() {
      return (
        this.dictionaryBhUsersRoleIds[this.getRolename] ===
          this.dictionaryBhUsersRoleIds.Admin ||
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Administrator ||
        this.dictionaryPracticeUserRole[this.getRolename] ===
          this.dictionaryPracticeUserRole.Advisor
      )
    },
    computedFeatureFlagsJournal() {
      return [
        {
          text: 'Journal & Metrics',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Active
          ],
        },
        {
          text: 'Journal only',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Partial
          ],
        },
        {
          text: 'Journal off',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Inactive
          ],
        },
      ]
    },
    computedFeatureFlagsSmartSurvey() {
      return [
        {
          text: 'Full survey',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Active
          ],
        },
        {
          text: 'Initial questions only',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Partial
          ],
        },
        {
          text: 'Survey off',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Inactive
          ],
        },
      ]
    },
    computedFeatureFlagsSwap() {
      return [
        {
          text: 'Active',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Active
          ],
        },
        {
          text: 'Inactive',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Inactive
          ],
        },
      ]
    },
    computedFeatureFlagsSurvey() {
      return [
        {
          text: 'Active',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Active
          ],
        },
        {
          text: 'Inactive',
          value: this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Inactive
          ],
        },
      ]
    },
    formIsValid() {
      return (
        this.newPatientData.firstName &&
        this.newPatientData.lastName &&
        this.newPatientData.email &&
        this.newPatientData.phone &&
        this.newPatientData.practiceUserId &&
        this.newPatientData.journalAvailable &&
        this.newPatientData.smartSurveyAvailable &&
        this.newPatientData.swapAvailable &&
        this.newPatientData.surveyAvailable
      )
    },
    advisors() {
      let advisorList = []
      if (
        this.dictionaryPracticeUserRole[this.getRolename] ===
        this.dictionaryPracticeUserRole.Advisor
      ) {
        let doctor = this.practiceDoctors.find(
          doctor => doctor.id == this.practiceUserId,
        )
        if (doctor) {
          doctor.fullName = doctor.firstName + ' ' + doctor.lastName
          advisorList.push(doctor)
        }
      } else {
        advisorList = this.practiceDoctors.map(doctor => ({
          ...doctor,
          fullName: `${doctor.firstName} ${doctor.lastName}`,
        }))
      }
      return advisorList
    },
    doctorQueryParams() {
      return {
        roleIds: [
          this.dictionaryPracticeUserRole.Advisor,
          this.dictionaryBhUsersRoleIds.Consultant,
        ],
        isActive: true,
        sortBy: 'fullName',
        isAvailable: true,
      }
    },
    doctorsQuery() {
      const params = new URLSearchParams()
      for (const [key, value] of Object.entries(this.doctorQueryParams)) {
        if (Array.isArray(value)) {
          value.forEach(item => {
            params.append(key, item)
          })
        } else if (value) {
          params.append(key, value)
        }
      }
      return params
    },
  },
  methods: {
    ...mapActions('practicesStates', ['getPracticeUsers']),
    closeDialog() {
      this.dialog = false
      this.$emit('close-dialog')
    },
    onClickOutside() {
      this.closeDialog()
    },
    onKeydown(key) {
      if (key.code === 'Escape' && key.isTrusted) this.closeDialog()
    },
    changeFeatureFlag(key, value) {
      this.onInput({ name: key, value })
    },
    onInput({ name, value }) {
      this.newPatientData[name] = value
    },
    clearForm() {
      this.$refs.form.reset()
      this.newPatientData = {
        ...emptyForm,
        practiceId: this.practiceId,
        practiceUserId: this.practiceUserId,
        journalAvailable: this.journalAvailable || emptyForm.journalAvailable,
        smartSurveyAvailable:
          this.smartSurveyAvailable || emptyForm.smartSurveyAvailable,
        swapAvailable: this.swapAvailable || emptyForm.swapAvailable,
        surveyAvailable: this.surveyAvailable || emptyForm.surveyAvailable,
      }
    },
    onSubmit() {
      this.$emit('on-submit', { ...this.newPatientData })
      this.clearForm()
      this.closeDialog()
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.new-practice {
  &-dialog {
    margin: $gutter;
    margin-left: auto;
    height: 100%;
    max-height: calc(100% - #{2 * $gutter}) !important;
    > .v-card {
      width: 100%;
      height: 100%;
      background-color: $bh-blue--4;
      overflow: auto;
    }
  }
  &-toolbar-title {
    color: $bh-blue;
    font-size: 1.5715rem;
    font-weight: bold;
  }
  &-card {
    &-title {
      font-size: $font-size-root;
      color: $bh-blue;
      padding-left: 1.5 * $gutter;
      padding-right: 1.5 * $gutter;
    }
    &-container {
      padding-left: 1.5 * $gutter;
      padding-right: 1.5 * $gutter;
    }
    &-footer {
      padding: 1.5 * $gutter;
    }
  }
  &-form {
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    &-body {
      ::v-deep .v-toolbar__content {
        padding-left: 1.5 * $gutter;
        padding-right: 1.5 * $gutter;
      }
    }
    &-col-left {
      padding-top: 0;
      padding-right: 0.25 * $gutter;
    }
    &-col-right {
      padding-top: 0;
      padding-left: 0.25 * $gutter;
    }
  }
}
</style>
